import { observable, action, computed } from 'mobx';
import XRegExp from 'xregexp';
import path from 'path';

import { AppManager, Variables } from 'core';

import { AutocompleteProfilesQuery } from 'apps/profile/gql';
import { AutocompleteInitiativesQuery } from 'apps/initiative/gql';
import {
  AutocompleteProfileProjectsQuery,
  AutocompleteInitiativeProjectsQuery,
} from 'apps/project/gql';
import {
  AutocompleteRepositoryFilesQuery,
  ContributionFileDetailsQuery,
} from 'apps/repository/gql';

import { SpaceSuggestionModel, DirectoryFileModel } from 'apps/finder/app';
import {
  ContributionFileModel,
  ContributionFileApp,
} from 'apps/repository/app';

import { ProjectModel } from 'apps/project/app';

export const usernameRegex = '(^|\\s)(?<username>[@+][\\p{L}\\w-]*)';
export const projectRegex = '(?<project>[\\p{L}\\w-]+)';
export const fileRegex =
  '(?<filepath>[ \\(\\)\\[\\]+%&\\.\\p{L}\\w\\d/-]+)(#(?<pathhash>[a-f\\d]+))?';
export const absoluteFile = `${usernameRegex}((?<userDone>/)(${projectRegex})?)?((?<projectDone>/)(${fileRegex})?)?`;
const inputPattern = XRegExp(absoluteFile, 'g');

const MAX_SUGGESTIONS = 100;

export class FinderAppManager extends AppManager {
  static registryName = 'FinderAppManager';
  @observable input = '';
  @observable users = [];
  @observable projects = [];
  @observable folders = [];
  @observable pathQueries = [];

  @observable selectedSuggestion = 0;
  @observable autocompleteVisible = false;

  constructor(parent, init = {}) {
    super(parent, init);

    this.AppRef('file', ContributionFileApp);

    if (this.initialPath) {
      this.input = this.initialPath;
    }

    this.variables = new Variables({
      username: () => this.params.username,
      space: () => this.params.username,
      slug: () => this.params.project,
      basepath: () => this.params.basepath,
      complete: () => this.params.complete,
      first: () => MAX_SUGGESTIONS,
    });

    this.variables.onChange(['space'], this.handleSpaceChange);
    this.variables.onChange(['slug'], this.handleProjectChange);
    this.variables.onChange(['basepath'], this.handlePathChange);
    this.variables.onChange(['complete'], this.handleSuggestionChange);
  }

  @computed get columns() {
    return this.folders.length + (this.file ? 1 : 0);
  }

  @computed
  get spaceQueryLoading() {
    const profileQ = this.queries.get('autocompleteProfile');
    const orgQ = this.queries.get('autocompleteInitiative');
    return profileQ?.loading || orgQ?.loading;
  }

  @computed
  get projectsQueryLoading() {
    const profileProjectsQ = this.queries.get('autocompleteProfileProjects');
    const orgProjectsQ = this.queries.get('autocompleteInitiativeProjects');
    return profileProjectsQ?.loading || orgProjectsQ?.loading;
  }

  @computed
  get filesQueryLoading() {
    const repoFilesQ = this.queries.get('autocompleteRepositoryFiles');
    return repoFilesQ?.loading;
  }

  @computed
  get fileQueryLoading() {
    const fileDetailsQ = this.queries.get('fileDetails');
    return fileDetailsQ?.loading;
  }

  @computed get pathQueriesLoading() {
    let loading = true;
    this.pathQueries.forEach(query => {
      loading = loading || query?.loading;
    });
    return loading;
  }

  @action handleSpaceChange = () => {
    const {
      variables,
      params: { username },
    } = this;
    if (!username) return;
    const firstCharacter = username && username[0];
    switch (firstCharacter) {
      case '+':
        this.initiativeProjectsQuery = new AutocompleteInitiativeProjectsQuery(
          this,
          { queryKey: 'autocompleteInitiativeProjects', variables },
        );
        this.initiativeProjectsQuery.process(
          this.WrapProjects,
          this.LoadProjects,
        );
        this.initiativeProjectsQuery.query();
        break;
      case '@':
        this.profileProjectsQuery = new AutocompleteProfileProjectsQuery(this, {
          queryKey: 'autocompleteProfileProjects',
          variables,
        });
        this.profileProjectsQuery.process(this.WrapProjects, this.LoadProjects);
        this.profileProjectsQuery.query();
        break;
      default:
        break;
    }
  };

  @action handleProjectChange = () => {
    this.folders = [];
    const {
      variables,
      params: { project },
    } = this;
    if (!project) return;
    this.repositoryFilesQuery = new AutocompleteRepositoryFilesQuery(this, {
      queryKey: 'autocompleteRepositoryFiles',
      variables,
    });
    this.repositoryFilesQuery.process(
      result =>
        result?.files
          ? result.files.map(file => DirectoryFileModel.Load(file))
          : [],
      files => this.LoadPathSegment(files, 0),
    );
    this.repositoryFilesQuery.query();
  };

  @action handlePathChange = () => {
    if (!this.params.basepath) {
      return;
    }
    const pathSegments = ['', ...this.params.basepath.split('/')];
    if (this.folders.length < pathSegments.length) {
      const arrayExpansion = new Array(
        pathSegments.length - this.folders.length,
      );
      this.folders = [...this.folders, ...arrayExpansion];
    }
    pathSegments.forEach((segment, i) => {
      const space = () => this.params.username;
      const slug = () => this.params.project;
      const basepath = () =>
        i === 0 ? '' : pathSegments.slice(1, i + 1).join('/');
      /* if (this.pathQueries.length > i) {
        const { variables } = this.pathQueries[i];
        if (
          variables.space === space() &&
          variables.slug === slug() &&
          variables.basepath === basepath()
        ) {
          return;
        }
      } */
      const variables = new Variables({
        space,
        slug,
        basepath,
      });
      const repositoryFilesQuery = new AutocompleteRepositoryFilesQuery(this, {
        variables,
      });
      repositoryFilesQuery.process(
        result =>
          result?.files
            ? result.files.map(file => DirectoryFileModel.Load(file))
            : [],
        files => this.LoadPathSegment(files, i),
      );
      this.pathQueries[i] = repositoryFilesQuery;

      this.folders[i] = { files: [] };
      repositoryFilesQuery.query();
    });
  };

  @action handleSuggestionChange = () => {
    this.file = null;
    const { username, project, basepath, complete } = this.params;
    if (!complete) {
      // FIXME - Is there a bug related to onChange?
      // Sometimes, handleSuggestionChanges without handlePathChange, even if both change
      if (basepath) this.handlePathChange();
      else if (project) this.handleProjectChange();
      else if (username) this.handleSpaceChange();
      return;
    }
    if (basepath || basepath === '') {
      const newVariables = new Variables({
        space: () => username,
        slug: () => project,
        filepath: () => `${basepath}/${complete}`,
      });
      this.fileQuery = new ContributionFileDetailsQuery(this, {
        queryKey: 'fileDetails',
        fetchPolicy: 'no-cache',
        variables: newVariables,
      });
      this.fileQuery.process(
        result => (result ? ContributionFileModel.Load(result) : null),
        this.LoadFilePreview,
        { subPath: 'contributionUpstream.contribFile' },
      );
      this.fileQuery.query();
    } else if (!username) {
      const newVariables = new Variables({
        complete: () => complete.substring(1),
        first: () => MAX_SUGGESTIONS,
      });
      const firstCharacter = complete && complete[0];
      switch (firstCharacter) {
        case '+':
          this.initiativesQuery = new AutocompleteInitiativesQuery(this, {
            queryKey: 'autocompleteInitiative',
            variables: newVariables,
          });
          this.initiativesQuery.process(
            this.WrapSpaceSuggestions,
            this.LoadSpaceSuggestions,
          );
          this.initiativesQuery.query();
          break;
        case '@':
          this.profilesQuery = new AutocompleteProfilesQuery(this, {
            queryKey: 'autocompleteProfile',
            variables: newVariables,
          });
          this.profilesQuery.process(
            this.WrapSpaceSuggestions,
            this.LoadSpaceSuggestions,
          );
          this.profilesQuery.query();
          break;
        default:
          break;
      }
      this.autocompleteVisible = true;
    } else {
      this.autocompleteVisible = false;
    }
  };

  WrapSpaceSuggestions = spaces => {
    return spaces
      ? spaces.map(space => SpaceSuggestionModel.Load(space.node))
      : null;
  };

  WrapProjects = projects => {
    return projects
      ? projects.map(project => ProjectModel.Load(project.node))
      : null;
  };

  @action LoadSpaceSuggestions = suggestedUsers => {
    if (!suggestedUsers) return;
    this.users = suggestedUsers;
    this.selectedSuggestion = 0;
  };

  @action LoadProjects = projects => {
    if (!projects) return;
    this.projects = projects;
  };

  @action LoadPathSegment = (files, i) => {
    this.folders[i] = { files };
  };

  @action LoadFilePreview = file => {
    if (!file) return;
    this.file = file;
  };

  @computed get params() {
    const matches = XRegExp.exec(this.input, inputPattern);
    if (!matches) return {};
    const { username, project, filepath } = matches;
    const { userDone, projectDone } = matches;
    const defaults = {
      username: null,
      project: null,
      basepath: null,
      complete: null,
    };
    if (filepath) {
      if (filepath[filepath.length - 1] === '/') {
        const basepath = filepath.slice(0, -1);
        return { ...defaults, username, project, basepath, complete: '' };
      }
      const dirname = path.dirname(filepath);
      const basepath = dirname === '.' ? '' : dirname;
      const complete = path.basename(filepath);
      return { ...defaults, username, project, basepath, complete };
    }
    if (project) {
      return projectDone
        ? { ...defaults, username, project, basepath: '', complete: '' }
        : { ...defaults, username, complete: project };
    }
    return userDone
      ? { ...defaults, username, complete: '' }
      : { ...defaults, complete: username };
  }

  @action SetInput = value => {
    this.input = value;
  };

  @action ClearInput = inputRef => {
    this.input = '';
    inputRef.focus();
  };

  @action OnInputChange = e => {
    this.SetInput(e.target.value);
  };

  @action SelectFile = (file, i) => {
    const { username, project } = this.params;
    const { dirname, filename } = file;
    const filePath = [dirname, filename].filter(e => !!e).join('/');
    const fullPath = `${username}/${project}/${filePath}`;
    const queryPath = file.isFolder ? `${fullPath}/` : fullPath;
    this.SetInput(queryPath);
    this.folders = this.folders.slice(0, i + 1);
  };

  @action SelectSuggestion = i => {
    this.selectedSuggestion = i;
  };

  @action PickSuggestion = text => {
    const selectedText = text || this.users[this.selectedSuggestion].text;
    this.input = `${selectedText}/`;
    this.autocompleteVisible = false;
  };

  @action DiscardSuggestion = () => {
    this.autocompleteVisible = false;
    this.selectedSuggestion = 0;
  };
}
